import {
  buildPath,
  buildTemplatePath,
  Button,
  Card,
  CloseButton,
  deepCopy,
  ListContext,
  ListContextValue,
  newToast,
  Page,
  PageRoute,
  pn,
  Stack,
  Typography,
  useUnsavedChanges,
  useValidationErrors,
} from "@compass/core";
import { SubmitButton } from "components";
import {
  BulkSecondLevelValidation,
  BulkSelectedItemErrors,
  StudentBulkCreateServices,
  StudentBulkCreateServicesResource,
  StudentService,
} from "generated/openApi";
import { useRouter } from "hooks";
import produce from "immer";
import {
  ProviderCreateContext,
  providerCreatePageRoute,
} from "pages/providers/providers/ProviderCreatePage/ProviderCreatePage";
import { CollegeContext, collegePageRoute } from "pages/system/utility/colleges/CollegePage/CollegePage";
import { useContext, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { setAppToast } from "redux/reducers/appSlice";
import ApiService from "services/apiService";
import { SelectedStudents } from "./SelectedStudents";
import { ServiceInfo } from "./ServiceInfo/ServiceInfo";
import {
  StudentServiceIndexListContext,
  studentServiceIndexListPageRoute,
} from "pages/students/student-service-indexes/StudentServiceIndexListPage/StudentServiceIndexListPage";

const routeName = "studentBulkCreateServices";

export const studentBulkCreateServicesPageRoute: PageRoute = {
  handle: "New Services",
  templatePath: buildTemplatePath(routeName),
  getPath: () => buildPath(routeName),
};

export type StudentBulkCreateServicesPageState = {
  selectedIds: number[];
};

export const StudentBulkCreateServicesPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { matchTemplatePath } = useRouter();
  const listContext = useContext<ListContextValue>(ListContext);
  const { state } = useLocation();
  const [stateParam] = useState<StudentBulkCreateServicesPageState>(state);
  const [bulkCreateServices, setBulkCreateServices] = useState<StudentBulkCreateServices>({});
  const [resource, setResource] = useState<StudentBulkCreateServicesResource>({});
  const { unsavedChanges, resetUnsavedChanges } = useUnsavedChanges({
    trackedItems: [bulkCreateServices],
  });
  const { validationErrors, setValidationErrors } = useValidationErrors();
  const [selectedItemsErrors, setSelectedItemsErrors] = useState<BulkSelectedItemErrors[]>([]);

  useEffect(() => {
    getBulkCreateServices(stateParam.selectedIds);
    fetchResource();
  }, []);

  const fetchResource = () => {
    ApiService.students.getBulkCreateServicesResource().then((response) => setResource(response));
  };

  const getBulkCreateServices = (studentIds: number[]) => {
    ApiService.students.getBulkCreateServices(studentIds).then((response) => {
      setBulkCreateServices(response);
      resetUnsavedChanges([response]);
    });
  };

  const handleServiceIndexSelect = (id: number) => {
    ApiService.studentServiceIndexes.get(id).then((response) => {
      setBulkCreateServices(
        produce(bulkCreateServices, (draft) => {
          draft.service!.serviceTypeId = response.serviceTypeId!;
          draft.service!.serviceSubTypeId = response.serviceSubTypeId!;
          draft.service!.name = response.name;
          draft.service!.providers = [];

          if (response.providerId) {
            const provider = resource.providers?.find((p) => p.id === response.providerId);
            if (provider) draft.service!.providers = [provider];
          }
        })
      );
    });
  };

  const openServiceIndex = () => {
    navigate({
      pathname: studentServiceIndexListPageRoute.getPath({ screenType: "dialog" }),
      search: studentServiceIndexListPageRoute.getOptionalParams?.({
        selectMode: true,
      }),
    });
  };

  const save = (bulkCreateServices: StudentBulkCreateServices) => {
    setValidationErrors({});
    setSelectedItemsErrors([]);

    ApiService.students
      .saveBulkCreateServices(bulkCreateServices)
      .then((response) => {
        const bulkSecondLevelValidation: BulkSecondLevelValidation | undefined =
          response as unknown as BulkSecondLevelValidation;

        if (bulkSecondLevelValidation && bulkSecondLevelValidation.badRequest) {
          setSelectedItemsErrors(bulkSecondLevelValidation.selectedItemsErrors || []);
          dispatch(setAppToast(newToast("error")));
        } else {
          let _bulkCreateServices = deepCopy(bulkCreateServices);
          _bulkCreateServices.service!.studentSignatureUploadFile = undefined;
          _bulkCreateServices.service!.staffName = undefined;
          _bulkCreateServices.service!.staffSignatureUploadFile = undefined;
          setBulkCreateServices(_bulkCreateServices);
          resetUnsavedChanges([_bulkCreateServices]);
          listContext.loadList?.();
          listContext.clearSelectedIds?.();
          dispatch(setAppToast(newToast("success")));
        }
      })
      .catch((ex) => {
        dispatch(setAppToast(newToast("error")));
        setValidationErrors(ex.data);
      });
  };

  const header = (
    <Stack direction="row" justifyContent="space-between">
      <Typography variant="inherit">{studentBulkCreateServicesPageRoute.handle}</Typography>

      <Stack direction="row" alignItems="center" spacing="sm">
        <CloseButton confirm={unsavedChanges} />

        <Button onClick={() => openServiceIndex()} variant="outlined">
          Index
        </Button>

        <SubmitButton onClick={() => save(bulkCreateServices)} disabled={!unsavedChanges} />
      </Stack>
    </Stack>
  );

  return (
    <>
      <Page isForm header={header}>
        <Stack spacing="lg">
          <Card header="Services">
            <Stack direction="column" spacing="md">
              <ServiceInfo
                service={bulkCreateServices.service || ({} as StudentService)}
                setService={(value) =>
                  setBulkCreateServices(
                    produce(bulkCreateServices, (draft) => {
                      draft.service = value;
                    })
                  )
                }
                additionalDates={bulkCreateServices.additionalDates || []}
                setAdditionalDates={(value) =>
                  setBulkCreateServices(
                    produce(bulkCreateServices, (draft) => {
                      draft.additionalDates = value;
                    })
                  )
                }
                additionalEntries={bulkCreateServices.additionalEntries || []}
                setAdditionalEntries={(value) =>
                  setBulkCreateServices(
                    produce(bulkCreateServices, (draft) => {
                      draft.additionalEntries = value;
                    })
                  )
                }
                resource={resource}
                validationErrors={validationErrors[pn<StudentBulkCreateServices>("service")] || {}}
                additionalEntriesValidationErrors={
                  validationErrors[pn<StudentBulkCreateServices>("additionalEntries")] || []
                }
              />
            </Stack>
          </Card>

          <SelectedStudents
            students={new Map(bulkCreateServices.students?.map((x) => [x.id, x]))}
            setStudents={(value) =>
              setBulkCreateServices({ ...bulkCreateServices, students: Array.from(value.values()) })
            }
            resource={resource}
            errors={selectedItemsErrors}
            setErrors={setSelectedItemsErrors}
          />
        </Stack>
      </Page>

      <ProviderCreateContext.Provider
        value={{
          onCreate: (id) => {
            fetchResource();
            ApiService.providers.get(id).then((response) => {
              setBulkCreateServices(
                produce(bulkCreateServices, (draft) => {
                  draft.service!.providers = draft.service!.providers || [];
                  draft.service!.providers.push(response);
                })
              );
            });
          },
        }}
      >
        {matchTemplatePath(providerCreatePageRoute.templatePath) && <Outlet />}
      </ProviderCreateContext.Provider>

      <CollegeContext.Provider
        value={{
          onCreate: (id) => {
            fetchResource();
            ApiService.colleges.get(id).then((response) => {
              setBulkCreateServices(
                produce(bulkCreateServices, (draft) => {
                  draft.service!.colleges = draft.service!.colleges || [];
                  draft.service!.colleges.push(response);
                })
              );
            });
          },
        }}
      >
        {matchTemplatePath(collegePageRoute.templatePath) && <Outlet />}
      </CollegeContext.Provider>

      <StudentServiceIndexListContext.Provider
        value={{
          onSelectIndex: handleServiceIndexSelect,
        }}
      >
        {matchTemplatePath(studentServiceIndexListPageRoute.templatePath) && <Outlet />}
      </StudentServiceIndexListContext.Provider>
    </>
  );
};
